/**
 * Employee Sync Tab
 *
 * Admin-only tab for synchronizing employees with MySQL users.
 * Shows sync status, allows manual sync with preview, and displays new available users.
 */

import { useState, useEffect } from 'react';
import {
  getSyncStatus,
  getNewUsers,
  getSyncPreview,
  syncSelectedEmployees,
  cleanupInvalidEmployees,
  type SyncStatusResponse,
  type NewUsersResponse,
  type SyncResult,
  type SyncPreviewResponse,
  type CleanupResult,
} from '../../../lib/syncApi';

export function EmployeeSyncTab() {
  const [syncStatus, setSyncStatus] = useState<SyncStatusResponse | null>(null);
  const [newUsers, setNewUsers] = useState<NewUsersResponse | null>(null);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [preview, setPreview] = useState<SyncPreviewResponse | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<number>>(new Set());
  const [selectedNewUserIds, setSelectedNewUserIds] = useState<Set<number>>(new Set());
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Cleanup state
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statusData, usersData] = await Promise.all([
        getSyncStatus(),
        getNewUsers(),
      ]);
      setSyncStatus(statusData);
      setNewUsers(usersData);
    } catch (err) {
      setError('Kon sync status niet laden');
      console.error('Error loading sync data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load preview when "Synchroniseer Nu" is clicked
  const handleStartSync = async () => {
    try {
      setLoadingPreview(true);
      setError(null);
      const previewData = await getSyncPreview();
      setPreview(previewData);

      // Pre-select all employees and new users by default
      setSelectedEmployeeIds(new Set(previewData.employees.map(e => e.employeeId)));
      setSelectedNewUserIds(new Set(previewData.newUsers.map(u => u.userId)));
      setShowPreviewModal(true);
    } catch (err) {
      setError('Kon sync voorstel niet laden');
      console.error('Error loading preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Execute sync with selected employees and new users
  const handleConfirmSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const employeeIds = Array.from(selectedEmployeeIds);
      const newUserIds = Array.from(selectedNewUserIds);
      const result = await syncSelectedEmployees(employeeIds, newUserIds);
      setLastSyncResult(result);
      setShowPreviewModal(false);
      setPreview(null);
      setSelectedEmployeeIds(new Set());
      setSelectedNewUserIds(new Set());
      // Reload data after sync
      await loadData();
    } catch (err) {
      setError('Synchronisatie mislukt');
      console.error('Error syncing:', err);
    } finally {
      setSyncing(false);
    }
  };

  // Toggle employee selection
  const toggleEmployeeSelection = (employeeId: number) => {
    setSelectedEmployeeIds(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  // Select/deselect all employees
  const toggleSelectAllEmployees = () => {
    if (preview) {
      if (selectedEmployeeIds.size === preview.employees.length) {
        setSelectedEmployeeIds(new Set());
      } else {
        setSelectedEmployeeIds(new Set(preview.employees.map(e => e.employeeId)));
      }
    }
  };

  // Toggle new user selection
  const toggleNewUserSelection = (userId: number) => {
    setSelectedNewUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Select/deselect all new users
  const toggleSelectAllNewUsers = () => {
    if (preview) {
      if (selectedNewUserIds.size === preview.newUsers.length) {
        setSelectedNewUserIds(new Set());
      } else {
        setSelectedNewUserIds(new Set(preview.newUsers.map(u => u.userId)));
      }
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowPreviewModal(false);
    setPreview(null);
    setSelectedEmployeeIds(new Set());
    setSelectedNewUserIds(new Set());
  };

  // Cleanup invalid employees
  const handleCleanup = async () => {
    setShowCleanupConfirm(false);
    try {
      setCleaningUp(true);
      setError(null);
      const result = await cleanupInvalidEmployees();
      setCleanupResult(result);
      // Reload data after cleanup
      await loadData();
    } catch (err) {
      setError('Cleanup mislukt');
      console.error('Error cleaning up:', err);
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Medewerker Synchronisatie</h2>
          <p className="mt-1 text-sm text-gray-500">
            Synchroniseer medewerkers met de MySQL gebruikersdatabase
          </p>
        </div>
        <button
          onClick={handleStartSync}
          disabled={loadingPreview}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loadingPreview ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Laden...
            </>
          ) : (
            <>
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Synchroniseer Nu
            </>
          )}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Status Overview Cards */}
      {syncStatus && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{syncStatus.summary.total}</div>
            <div className="text-sm text-gray-500">Totaal medewerkers</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{syncStatus.summary.active}</div>
            <div className="text-sm text-gray-500">Actief</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">{syncStatus.summary.inactive}</div>
            <div className="text-sm text-gray-500">Inactief</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-400">{syncStatus.summary.archived}</div>
            <div className="text-sm text-gray-500">Gearchiveerd</div>
          </div>
          <div className={`border rounded-lg p-4 ${syncStatus.summary.needsSync > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
            <div className={`text-2xl font-bold ${syncStatus.summary.needsSync > 0 ? 'text-orange-600' : 'text-green-600'}`}>
              {syncStatus.summary.needsSync}
            </div>
            <div className="text-sm text-gray-500">Sync nodig</div>
          </div>
        </div>
      )}

      {/* New Users Available */}
      {newUsers && newUsers.count > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {newUsers.count} nieuwe gebruiker(s) beschikbaar
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>De volgende MySQL gebruikers hebben nog geen medewerker profiel:</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  {newUsers.users.slice(0, 5).map((user) => (
                    <li key={user.id}>
                      <span className="font-medium">{user.name}</span> ({user.email})
                    </li>
                  ))}
                  {newUsers.users.length > 5 && (
                    <li className="text-blue-600">...en {newUsers.users.length - 5} meer</li>
                  )}
                </ul>
                <p className="mt-2 text-xs">
                  Ga naar Medewerkers om nieuwe profielen aan te maken.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Last Sync Result */}
      {lastSyncResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Synchronisatie voltooid</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>Laatste sync: {new Date(lastSyncResult.timestamp).toLocaleString('nl-NL')}</p>
                <ul className="mt-1 space-y-1">
                  {lastSyncResult.summary.createdProfiles > 0 && (
                    <li className="text-green-800 font-medium">
                      {lastSyncResult.summary.createdProfiles} nieuw profiel{lastSyncResult.summary.createdProfiles !== 1 ? 'en' : ''} aangemaakt
                    </li>
                  )}
                  {lastSyncResult.summary.syncedToActive > 0 && (
                    <li>{lastSyncResult.summary.syncedToActive} medewerker(s) geactiveerd</li>
                  )}
                  {lastSyncResult.summary.syncedToInactive > 0 && (
                    <li>{lastSyncResult.summary.syncedToInactive} medewerker(s) op inactief gezet</li>
                  )}
                  {lastSyncResult.summary.syncedToArchived > 0 && (
                    <li>{lastSyncResult.summary.syncedToArchived} medewerker(s) gearchiveerd</li>
                  )}
                  {lastSyncResult.summary.createdProfiles === 0 &&
                   lastSyncResult.summary.syncedToActive === 0 &&
                   lastSyncResult.summary.syncedToInactive === 0 &&
                   lastSyncResult.summary.syncedToArchived === 0 && (
                    <li>Geen wijzigingen nodig - alles is up-to-date</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employees needing sync */}
      {syncStatus && syncStatus.employees.filter(e => e.needsSync).length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Medewerkers die sync nodig hebben</h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Medewerker
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Huidige status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MySQL status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Laatste sync
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {syncStatus.employees
                  .filter(e => e.needsSync)
                  .map((employee) => (
                    <tr key={employee.employeeId}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{employee.employeeName}</div>
                        <div className="text-xs text-gray-500">ID: {employee.employeeId}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          employee.currentSyncStatus === 'active' ? 'bg-green-100 text-green-800' :
                          employee.currentSyncStatus === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {employee.currentSyncStatus === 'active' ? 'Actief' :
                           employee.currentSyncStatus === 'inactive' ? 'Inactief' : 'Gearchiveerd'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          employee.mysqlUserActive === true ? 'bg-green-100 text-green-800' :
                          employee.mysqlUserActive === false ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {employee.mysqlUserActive === true ? 'Actief' :
                           employee.mysqlUserActive === false ? 'Inactief' : 'Verwijderd'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {employee.lastSyncedAt
                          ? new Date(employee.lastSyncedAt).toLocaleString('nl-NL')
                          : 'Nooit'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cleanup Result */}
      {cleanupResult && (
        <div className={`rounded-lg p-4 ${cleanupResult.removedCount > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
          <div className="flex items-start">
            <svg className={`h-5 w-5 mt-0.5 ${cleanupResult.removedCount > 0 ? 'text-orange-400' : 'text-green-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${cleanupResult.removedCount > 0 ? 'text-orange-800' : 'text-green-800'}`}>
                Cleanup voltooid
              </h3>
              <div className={`mt-2 text-sm ${cleanupResult.removedCount > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                {cleanupResult.removedCount === 0 ? (
                  <p>Geen ongeldige medewerkers gevonden. Alles is in orde.</p>
                ) : (
                  <>
                    <p>{cleanupResult.removedCount} medewerker(s) verwijderd:</p>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      {cleanupResult.removedEmployees.map((emp) => (
                        <li key={emp.employeeId}>
                          <span className="font-medium">ID {emp.employeeId}</span> - {emp.reason}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <button
                onClick={() => setCleanupResult(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Hoe werkt synchronisatie?</h3>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li><strong>Actief:</strong> MySQL gebruiker bestaat en is actief</li>
          <li><strong>Inactief:</strong> MySQL gebruiker bestaat maar is inactief</li>
          <li><strong>Gearchiveerd:</strong> MySQL gebruiker is verwijderd - data blijft bewaard</li>
        </ul>
        <p className="mt-2 text-xs text-gray-500">
          Gearchiveerde medewerkers behouden hun beoordelingen en andere data.
          Dit voorkomt data verlies wanneer gebruikers worden verwijderd uit MySQL.
        </p>
      </div>

      {/* Cleanup Section */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-medium text-red-800">Database Opschonen</h3>
            <p className="mt-1 text-sm text-red-700">
              Verwijder medewerkers waarvan de MySQL gebruiker geen geldige organisatie (informationProvider) heeft.
              Dit zijn gebruikers die per ongeluk zijn geïmporteerd.
            </p>
          </div>
          <button
            onClick={() => setShowCleanupConfirm(true)}
            disabled={cleaningUp}
            className="ml-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cleaningUp ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Bezig...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Opschonen
              </>
            )}
          </button>
        </div>
      </div>

      {/* Cleanup Confirmation Modal */}
      {showCleanupConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowCleanupConfirm(false)}
            ></div>

            {/* Modal panel */}
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900">
                      Database Opschonen
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Weet je zeker dat je ongeldige medewerkers wilt verwijderen?
                        Dit verwijdert medewerkers waarvan de MySQL gebruiker geen geldige organisatie heeft.
                      </p>
                      <p className="mt-2 text-sm text-red-600 font-medium">
                        Deze actie kan niet ongedaan worden gemaakt.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={handleCleanup}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                >
                  Verwijderen
                </button>
                <button
                  type="button"
                  onClick={() => setShowCleanupConfirm(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Annuleren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Preview Modal */}
      {showPreviewModal && preview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseModal}
            ></div>

            {/* Modal panel */}
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900">
                      Sync Voorstel
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Selecteer de medewerkers die je wilt synchroniseren
                    </p>

                    {preview.count === 0 && preview.newUsersCount === 0 ? (
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700">
                          Alle medewerkers zijn al gesynchroniseerd. Er zijn geen wijzigingen nodig.
                        </p>
                      </div>
                    ) : (
                      <div className="mt-4 max-h-96 overflow-y-auto">
                        {/* New Users Section */}
                        {preview.newUsersCount > 0 && (
                          <div className="mb-4">
                            <div className="flex items-center border-b border-green-200 pb-3 mb-3">
                              <input
                                type="checkbox"
                                id="select-all-new"
                                checked={selectedNewUserIds.size === preview.newUsers.length && preview.newUsers.length > 0}
                                onChange={toggleSelectAllNewUsers}
                                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <label htmlFor="select-all-new" className="ml-2 text-sm font-medium text-green-800">
                                Nieuwe profielen aanmaken ({preview.newUsersCount} gebruiker{preview.newUsersCount !== 1 ? 's' : ''})
                              </label>
                            </div>
                            <div className="space-y-2">
                              {preview.newUsers.map((user) => (
                                <div
                                  key={user.userId}
                                  className={`flex items-start p-3 rounded-lg border ${
                                    selectedNewUserIds.has(user.userId)
                                      ? 'border-green-300 bg-green-50'
                                      : 'border-gray-200 bg-white'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    id={`new-user-${user.userId}`}
                                    checked={selectedNewUserIds.has(user.userId)}
                                    onChange={() => toggleNewUserSelection(user.userId)}
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                  />
                                  <div className="ml-3 flex-1">
                                    <label
                                      htmlFor={`new-user-${user.userId}`}
                                      className="block text-sm font-medium text-gray-900 cursor-pointer"
                                    >
                                      {user.userName}
                                    </label>
                                    <p className="text-xs text-gray-500">{user.userEmail}</p>
                                    <div className="mt-1 flex items-center gap-2 text-xs">
                                      <span className="inline-flex px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                                        Nieuw profiel
                                      </span>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">{user.changeDescription}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Existing Employees Section */}
                        {preview.count > 0 && (
                          <div>
                            <div className="flex items-center border-b border-gray-200 pb-3 mb-3">
                              <input
                                type="checkbox"
                                id="select-all-employees"
                                checked={selectedEmployeeIds.size === preview.employees.length && preview.employees.length > 0}
                                onChange={toggleSelectAllEmployees}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor="select-all-employees" className="ml-2 text-sm font-medium text-gray-900">
                                Status wijzigingen ({preview.count} medewerker{preview.count !== 1 ? 's' : ''})
                              </label>
                            </div>
                            <div className="space-y-2">
                              {preview.employees.map((emp) => (
                                <div
                                  key={emp.employeeId}
                                  className={`flex items-start p-3 rounded-lg border ${
                                    selectedEmployeeIds.has(emp.employeeId)
                                      ? 'border-blue-300 bg-blue-50'
                                      : 'border-gray-200 bg-white'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    id={`emp-${emp.employeeId}`}
                                    checked={selectedEmployeeIds.has(emp.employeeId)}
                                    onChange={() => toggleEmployeeSelection(emp.employeeId)}
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <div className="ml-3 flex-1">
                                    <label
                                      htmlFor={`emp-${emp.employeeId}`}
                                      className="block text-sm font-medium text-gray-900 cursor-pointer"
                                    >
                                      {emp.employeeName}
                                    </label>
                                    <div className="mt-1 flex items-center gap-2 text-xs">
                                      <span className={`inline-flex px-2 py-0.5 rounded-full font-medium ${
                                        emp.currentSyncStatus === 'active' ? 'bg-green-100 text-green-700' :
                                        emp.currentSyncStatus === 'inactive' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {emp.currentSyncStatus === 'active' ? 'Actief' :
                                         emp.currentSyncStatus === 'inactive' ? 'Inactief' : 'Gearchiveerd'}
                                      </span>
                                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                      </svg>
                                      <span className={`inline-flex px-2 py-0.5 rounded-full font-medium ${
                                        emp.proposedSyncStatus === 'active' ? 'bg-green-100 text-green-700' :
                                        emp.proposedSyncStatus === 'inactive' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {emp.proposedSyncStatus === 'active' ? 'Actief' :
                                         emp.proposedSyncStatus === 'inactive' ? 'Inactief' : 'Gearchiveerd'}
                                      </span>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">{emp.changeDescription}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Selection summary */}
                        <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
                          {selectedNewUserIds.size > 0 && (
                            <span className="text-green-700">{selectedNewUserIds.size} nieuw profiel{selectedNewUserIds.size !== 1 ? 'en' : ''}</span>
                          )}
                          {selectedNewUserIds.size > 0 && selectedEmployeeIds.size > 0 && ' + '}
                          {selectedEmployeeIds.size > 0 && (
                            <span>{selectedEmployeeIds.size} status update{selectedEmployeeIds.size !== 1 ? 's' : ''}</span>
                          )}
                          {selectedNewUserIds.size === 0 && selectedEmployeeIds.size === 0 && 'Niets geselecteerd'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                {(preview.count > 0 || preview.newUsersCount > 0) && (
                  <button
                    type="button"
                    onClick={handleConfirmSync}
                    disabled={syncing || (selectedEmployeeIds.size === 0 && selectedNewUserIds.size === 0)}
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Synchroniseren...
                      </>
                    ) : (
                      `Synchroniseer (${selectedNewUserIds.size + selectedEmployeeIds.size} actie${selectedNewUserIds.size + selectedEmployeeIds.size !== 1 ? 's' : ''})`
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={syncing}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50"
                >
                  {preview.count === 0 && preview.newUsersCount === 0 ? 'Sluiten' : 'Annuleren'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
